import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CaretUp, CaretDown, DotsSixVertical, SortAscending } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MedewerkerVolgorde = ({ open, onOpenChange, medewerkers, onSaved }) => {
  const [lijst, setLijst] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sleep, setSleep] = useState(null);

  useEffect(() => {
    if (open) setLijst(medewerkers.map((m) => ({ id: m.id, naam: `${m.voornaam} ${m.achternaam}`, functie: m.functie })));
  }, [open, medewerkers]);

  const verplaats = (van, naar) => {
    if (naar < 0 || naar >= lijst.length) return;
    setLijst((l) => {
      const n = [...l];
      const [item] = n.splice(van, 1);
      n.splice(naar, 0, item);
      return n;
    });
  };

  const sorteerAZ = () => {
    setLijst((l) => [...l].sort((a, b) => a.naam.localeCompare(b.naam, "nl")));
    toast.success("Alfabetisch gesorteerd — nog opslaan");
  };

  const opslaan = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/medewerkers/volgorde`, { ids: lijst.map((m) => m.id) });
      toast.success("Volgorde opgeslagen");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-sm max-h-[90vh] flex flex-col" data-testid="volgorde-dialog">
        <DialogHeader>
          <DialogTitle>Volgorde medewerkers</DialogTitle>
          <DialogDescription className="text-xs">
            Sleep met de greep of gebruik de pijltjes. Deze volgorde geldt in Planning,
            Verlof, Maandoverzicht en de exports.
          </DialogDescription>
        </DialogHeader>

        <Button
          variant="outline"
          onClick={sorteerAZ}
          className="rounded-sm h-8 text-xs self-start"
          data-testid="volgorde-az-btn"
        >
          <SortAscending size={14} weight="bold" className="mr-1.5" />
          Sorteer op naam (A–Z)
        </Button>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1.5" data-testid="volgorde-lijst">
          {lijst.map((m, i) => (
            <div
              key={m.id}
              draggable
              onDragStart={() => setSleep(i)}
              onDragOver={(e) => {
                e.preventDefault();
                if (sleep !== null && sleep !== i) {
                  verplaats(sleep, i);
                  setSleep(i);
                }
              }}
              onDragEnd={() => setSleep(null)}
              className={`flex items-center gap-2 bg-white border rounded-sm px-2 py-2 ${
                sleep === i ? "border-[#002FA7] bg-[#EAF1FF]" : "border-border"
              }`}
              data-testid={`volgorde-item-${m.id}`}
            >
              <DotsSixVertical size={18} weight="bold" className="text-muted-foreground cursor-grab shrink-0" />
              <span className="w-6 text-[11px] font-bold text-muted-foreground shrink-0">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{m.naam}</p>
                {m.functie && <p className="text-[10px] text-muted-foreground truncate">{m.functie}</p>}
              </div>
              <button
                type="button"
                onClick={() => verplaats(i, i - 1)}
                disabled={i === 0}
                className="w-8 h-8 flex items-center justify-center border border-border rounded-sm disabled:opacity-30 active:bg-[#EAF1FF] shrink-0"
                data-testid={`volgorde-up-${m.id}`}
              >
                <CaretUp size={14} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => verplaats(i, i + 1)}
                disabled={i === lijst.length - 1}
                className="w-8 h-8 flex items-center justify-center border border-border rounded-sm disabled:opacity-30 active:bg-[#EAF1FF] shrink-0"
                data-testid={`volgorde-down-${m.id}`}
              >
                <CaretDown size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="volgorde-cancel">
            Annuleren
          </Button>
          <Button
            onClick={opslaan}
            disabled={saving}
            className="bg-[#002FA7] hover:bg-[#002280] text-white"
            data-testid="volgorde-save"
          >
            {saving ? "Opslaan..." : "Volgorde opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
